/**
 * URL utilities for callback and webhook URL construction
 */
import { API_ROUTES } from '../api-routes.js';
/**
 * Build staff call status callback URL with query parameters
 *
 * @param baseUrl - Base API URL
 * @param options - Callback options containing conference and call information
 * @returns Complete callback URL with query parameters
 *
 * @example
 * buildStaffCallStatusUrl("https://api.example.com", {
 *   conferenceSid: "CF123",
 *   originalCallSid: "CA456",
 *   phoneNumber: "+15551234567"
 * })
 * // Returns: "https://api.example.com/api/voice/staff-call/status?conferenceSid=CF123&originalCallSid=CA456&phoneNumber=%2B15551234567"
 */
export function buildStaffCallStatusUrl(baseUrl, options) {
    const { conferenceSid, originalCallSid, phoneNumber, queueName } = options;
    const queryParams = new URLSearchParams({
        conferenceSid,
        originalCallSid,
        phoneNumber,
    });
    // Add queueName if provided
    if (queueName) {
        queryParams.set('queueName', queueName);
    }
    return `${baseUrl}${API_ROUTES.VOICE.STAFF_CALL_STATUS}?${queryParams.toString()}`;
}
/**
 * Build staff conference status callback URL
 *
 * @param baseUrl - Base API URL
 * @returns Complete callback URL for conference status updates
 *
 * @example
 * buildStaffConferenceStatusUrl("https://api.example.com")
 * // Returns: "https://api.example.com/api/voice/staff-conference/status"
 */
export function buildStaffConferenceStatusUrl(baseUrl) {
    return `${baseUrl}${API_ROUTES.VOICE.STAFF_CONFERENCE_STATUS}`;
}
/**
 * Build webhook base URL from environment configuration
 * Uses dedicated webhook URL if available, falls back to API base URL
 *
 * @param webhookBaseUrl - Dedicated webhook base URL (optional)
 * @param apiBaseUrl - API base URL
 * @param fallbackUrl - Fallback URL if neither is available
 * @returns Resolved base URL for webhooks
 */
export function resolveWebhookBaseUrl(webhookBaseUrl, apiBaseUrl, fallbackUrl = 'https://staging.api.hiresuper.com') {
    return webhookBaseUrl || apiBaseUrl || fallbackUrl;
}
/**
 * Validate URL format
 *
 * @param url - URL to validate
 * @returns True if URL is valid, false otherwise
 */
export function isValidUrl(url) {
    try {
        new URL(url);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Validate that a string is an http(s) URL.
 *
 * Stricter than {@link isValidUrl} (which accepts any scheme, e.g. `mailto:`,
 * `data:`, `javascript:`). Use this for user-provided links that get surfaced
 * or sent externally — e.g. the listings fallback link texted to callers — so
 * client and server enforce the same rule.
 *
 * @param url - URL to validate
 * @returns True if the URL parses and uses http: or https:, false otherwise
 */
export function isHttpUrl(url) {
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    }
    catch {
        return false;
    }
}
/**
 * Build query string from parameters object
 *
 * @param params - Parameters object
 * @returns Query string (without leading ?)
 *
 * @example
 * buildQueryString({ foo: "bar", baz: "qux" })
 * // Returns: "foo=bar&baz=qux"
 */
export function buildQueryString(params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
    });
    return searchParams.toString();
}
//# sourceMappingURL=url-utils.js.map